import React, { useMemo, useState } from 'react';
import VendorCard from './VendorCard';
import { PlusIcon } from '../UI/Icons';
import { Vendor } from '../../utils/constants';
import venueSettingsService from '../../services/venueSettingsService';
import styles from './VendorList.module.css';

type StatusFilter = 'all' | 'active' | 'inactive';
type SortOption = 'name-asc' | 'items-desc' | 'wait-asc';

interface VendorListProps {
  vendors: Vendor[];
  loading?: boolean;
  onAddVendor: () => void;
  onManageVendor: (vendor: Vendor) => void;
  onEditSections: (vendor: Vendor) => void;
  onEditDetails?: (vendor: Vendor) => void;
  onToggleStatus?: (vendor: Vendor, status: 'active' | 'inactive') => void;
  onUpdateWait?: (vendor: Vendor, waitMinutes: number | null) => void;
  onDeleteVendor?: (vendor: Vendor) => void;
}

function VendorCardSkeleton() {
  return (
    <div className={styles.skeletonCard} aria-hidden="true">
      <div className={`${styles.skeletonBlock} ${styles.skeletonAvatar}`} />
      <div className={styles.skeletonBody}>
        <div className={`${styles.skeletonBlock} ${styles.skeletonTitle}`} />
        <div className={`${styles.skeletonBlock} ${styles.skeletonLine}`} />
        <div className={`${styles.skeletonBlock} ${styles.skeletonLine}`} />
        <div className={`${styles.skeletonBlock} ${styles.skeletonButton}`} />
      </div>
    </div>
  );
}

const VendorList: React.FC<VendorListProps> = ({
  vendors,
  loading = false,
  onAddVendor,
  onManageVendor,
  onEditSections,
  onEditDetails,
  onToggleStatus,
  onUpdateWait,
  onDeleteVendor,
}) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [cuisineFilter, setCuisineFilter] = useState('all');
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');
  const [cuisineOptions, setCuisineOptions] = useState<string[]>([]);

  React.useEffect(() => {
    venueSettingsService.get().then((data) => {
      const types = (data.cuisineTypes || []).map((c) => c.id).filter(Boolean);
      if (types.length) setCuisineOptions(types);
    }).catch(() => {
      // keep empty — filter hidden when no options
    });
  }, []);

  const filteredVendors = useMemo(() => {
    const query = search.trim().toLowerCase();

    let list = vendors.filter((vendor) => {
      const isActive = (vendor.status || 'active') === 'active';
      if (statusFilter === 'active' && !isActive) return false;
      if (statusFilter === 'inactive' && isActive) return false;

      if (cuisineFilter !== 'all' && vendor.cuisine_type !== cuisineFilter) return false;

      if (!query) return true;
      const haystack = [
        vendor.name,
        vendor.cuisine_type || '',
        ...(vendor.sections || []),
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });

    list = [...list].sort((a, b) => {
      if (sortBy === 'items-desc') {
        return (b.menuItems?.length || 0) - (a.menuItems?.length || 0);
      }
      if (sortBy === 'wait-asc') {
        const aw = a.currentWaitMinutes ?? 9999;
        const bw = b.currentWaitMinutes ?? 9999;
        return aw - bw;
      }
      return a.name.localeCompare(b.name);
    });

    return list;
  }, [vendors, search, statusFilter, cuisineFilter, sortBy]);

  const activeCount = vendors.filter((v) => (v.status || 'active') === 'active').length;
  const totalMenuItems = vendors.reduce((sum, v) => sum + (v.menuItems?.length || 0), 0);

  const hasFilters =
    search.trim() !== '' ||
    statusFilter !== 'all' ||
    cuisineFilter !== 'all';

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setCuisineFilter('all');
    setSortBy('name-asc');
  };

  const EmptyState = () => (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>🏪</div>
      <h3 className={styles.emptyTitle}>No vendors yet</h3>
      <p className={styles.emptyDescription}>
        Add your first vendor to assign stadium sections and build menus.
      </p>
      <button type="button" onClick={onAddVendor} className={styles.emptyButton}>
        <PlusIcon size={20} />
        Add your first vendor
      </button>
    </div>
  );

  const FilteredEmpty = () => (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>🔍</div>
      <h3 className={styles.emptyTitle}>No vendors match your filters</h3>
      <p className={styles.emptyDescription}>
        Try a different search or clear filters to see all vendors.
      </p>
      <button type="button" onClick={clearFilters} className={styles.emptyButtonSecondary}>
        Clear filters
      </button>
    </div>
  );

  return (
    <div className={styles.vendorList}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Vendor management</h1>
          <p className={styles.subtitle}>
            Manage restaurant vendors and their stadium section assignments
          </p>
        </div>

        {!loading && vendors.length > 0 && (
          <button type="button" onClick={onAddVendor} className={styles.addButton}>
            <PlusIcon size={20} />
            Add vendor
          </button>
        )}
      </div>

      {loading ? (
        <>
          <div className={styles.stats}>
            {[1, 2, 3].map((i) => (
              <div key={i} className={styles.stat}>
                <div className={`${styles.skeletonBlock} ${styles.skeletonStat}`} />
              </div>
            ))}
          </div>
          <div className={styles.grid}>
            {Array.from({ length: 6 }).map((_, i) => (
              <VendorCardSkeleton key={i} />
            ))}
          </div>
        </>
      ) : vendors.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <div className={styles.stats}>
            <div className={styles.stat}>
              <div className={`${styles.statValue} ${styles.statValuePrimary}`}>{vendors.length}</div>
              <div className={styles.statLabel}>Vendors</div>
            </div>
            <div className={styles.stat}>
              <div className={`${styles.statValue} ${styles.statValueSuccess}`}>{activeCount}</div>
              <div className={styles.statLabel}>Active now</div>
            </div>
            <div className={styles.stat}>
              <div className={`${styles.statValue} ${styles.statValueSecondary}`}>{totalMenuItems}</div>
              <div className={styles.statLabel}>Menu items</div>
            </div>
          </div>

          <div className={styles.toolbar}>
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Search vendors…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search vendors"
            />

            <div className={styles.pillGroup} role="group" aria-label="Filter by status">
              {(['all', 'active', 'inactive'] as StatusFilter[]).map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`${styles.pill} ${statusFilter === value ? styles.pillActive : ''}`}
                  onClick={() => setStatusFilter(value)}
                >
                  {value === 'all' ? 'All' : value === 'active' ? 'Active' : 'Paused'}
                </button>
              ))}
            </div>

            {cuisineOptions.length > 0 && (
              <select
                className={styles.select}
                value={cuisineFilter}
                onChange={(e) => setCuisineFilter(e.target.value)}
                aria-label="Filter by cuisine"
              >
                <option value="all">All cuisines</option>
                {cuisineOptions.map((c) => (
                  <option key={c} value={c}>
                    {c.replace(/[-_]/g, ' ')}
                  </option>
                ))}
              </select>
            )}

            <select
              className={styles.select}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              aria-label="Sort vendors"
            >
              <option value="name-asc">Name A→Z</option>
              <option value="items-desc">Most items</option>
              <option value="wait-asc">Wait time</option>
            </select>
          </div>

          {filteredVendors.length === 0 ? (
            <FilteredEmpty />
          ) : (
            <div className={styles.grid}>
              {filteredVendors.map((vendor) => (
                <VendorCard
                  key={vendor.id}
                  vendor={vendor}
                  onManage={() => onManageVendor(vendor)}
                  onEditSections={() => onEditSections(vendor)}
                  onEditDetails={onEditDetails ? () => onEditDetails(vendor) : undefined}
                  onToggleStatus={onToggleStatus}
                  onUpdateWait={onUpdateWait}
                  onDelete={onDeleteVendor}
                />
              ))}
            </div>
          )}

          {filteredVendors.length > 0 && hasFilters && (
            <p className={styles.resultCount}>
              Showing {filteredVendors.length} of {vendors.length} vendors
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default VendorList;
